/**
 * Talents Slice for Character Store
 *
 * Manages talent operations using string-based IDs exclusively.
 * Talents are stored with complete data (self-contained) including purchaseHistory.
 * Special handling for "Talented" talent which affects skills with +10 bonus.
 *
 * Requirements: 2.1, 2.2, 2.5, 3.2, 4.2, 4.7, 5.6
 */

import { StateCreator } from 'zustand';
import type { Character, Talent, TalentPurchase, Trait } from '../../../types/character';
import { TalentsService } from '../../../services/TalentsService';
import { getHighestPsyRating } from '../../../services/TalentsService';
import { ensureTalentFields as ensureTalentFieldsHelper } from '../../../utils/purchaseRecordHelpers';

/**
 * Talents slice interface defining all talent-related operations.
 * All operations use string IDs for talent identification.
 *
 * Requirements: 1.2, 2.1, 2.2, 2.5, 3.2, 4.2, 4.7, 5.6
 */
export interface TalentsSlice {
  /**
   * Add a new talent to the character.
   * Ensures purchaseHistory and purchaseCount are initialized.
   * For "Talented" talent, affectedSkillId should be provided.
   *
   * @param talent - The talent to add (must include string id)
   * @param advancementId - Optional advancement ID for tracking purchase history
   * @param affectedSkillId - For "talented" talent - which skill gets +10
   *
   * Requirements: 2.5, 3.2, 4.2, 5.6
   */
  addTalent: (talent: Talent, advancementId?: string, affectedSkillId?: string) => void;

  /**
   * Add a talent purchase to track advancement.
   * Creates the talent if it doesn't exist, or updates existing talent's purchaseHistory.
   * For "Talented" talent, affectedSkillId is required.
   *
   * @param talentId - The string ID of the talent (e.g., "air-of-authority", "talented")
   * @param advancementId - The unique ID of the advancement being purchased
   * @param rank - The career rank when this advancement was purchased
   * @param affectedSkillId - For "talented" talent - which skill gets +10
   *
   * Requirements: 2.1, 2.2, 2.5, 3.2, 4.2, 4.7, 5.6
   */
  addTalentPurchase: (
    talentId: string,
    advancementId: string,
    rank: number,
    affectedSkillId?: string
  ) => void;

  /**
   * Remove a talent purchase from a character's talent.
   * Updates the purchase count based on remaining purchases.
   * If no purchases remain, the talent is removed.
   *
   * @param talentId - The string ID of the talent
   * @param advancementId - The advancement ID to remove
   *
   * Requirements: 3.2
   */
  removeTalentPurchase: (talentId: string, advancementId: string) => void;

  /**
   * Remove a talent from the character by its string ID.
   *
   * @param talentId - The string ID of the talent to remove
   *
   * Requirements: 1.2
   */
  removeTalent: (talentId: string) => void;

  /**
   * Get a talent by its string ID from the character's talents.
   *
   * @param talentId - The string ID of the talent
   * @returns The talent or undefined if not found
   *
   * Requirements: 1.2, 2.1
   */
  getTalentById: (talentId: string) => Talent | undefined;

  /**
   * Check if a talent has a specific advancement purchase.
   *
   * @param talentId - The string ID of the talent
   * @param advancementId - The advancement ID to check for
   * @returns True if the talent has this advancement purchase
   *
   * Requirements: 3.2
   */
  hasTalentPurchase: (talentId: string, advancementId: string) => boolean;

  /**
   * Check if a skill already has the "Talented" bonus applied.
   *
   * @param skillId - The string ID of the skill to check
   * @returns True if the skill already has "Talented" applied
   *
   * Requirements: 4.3, 4.4
   */
  skillHasTalentedBonus: (skillId: string) => boolean;

  /**
   * Get all skills that have the "Talented" bonus.
   *
   * @returns Array of skill IDs that have "Talented" applied
   *
   * Requirements: 4.3
   */
  getSkillsWithTalentedBonus: () => string[];

  /**
   * Update the Machine trait based on total Machine Trait granting talent levels.
   * Used by both "The Flesh is Weak" and "Physical Perfection" talents.
   * Internal helper method.
   */
  updateMachineTrait: (character: Character, fleshIsWeakCount: number) => Character;

  /**
   * Add a trait to the character.
   *
   * @param trait - The trait to add
   */
  addTrait: (trait: Trait) => void;

  /**
   * Remove a trait from the character by its string ID.
   *
   * @param traitId - The string ID of the trait to remove
   */
  removeTrait: (traitId: string) => void;
}

// Helper interface to access the main character state
interface CharacterStore extends TalentsSlice {
  character: Character | null;
  hasUnsavedChanges: boolean;
}

/** Re-export shared helper as local name for use within this slice. */
const ensureTalentFields = ensureTalentFieldsHelper;

/**
 * Recompute psychicProfile.psyRating from the character's talent list.
 * Uses getHighestPsyRating to derive the value from psy-rating-N talents.
 *
 * Requirements: 3.1, 3.3, 3.4
 */
function recomputePsyRating(character: Character): Character {
  const psyRating = getHighestPsyRating(character.talents);
  const progression = character.progression || {};
  return {
    ...character,
    progression: {
      ...progression,
      psychicProfile: {
        ...(progression as any).psychicProfile,
        psyRating
      }
    }
  } as Character;
}

/** Talents that grant Machine Trait levels (one level per purchase). */
const MACHINE_TRAIT_TALENT_IDS = new Set(['the-flesh-is-weak', 'physical-perfection']);

/** Check if a talent grants Machine Trait levels. */
function grantsMachineTrait(talentId: string): boolean {
  return MACHINE_TRAIT_TALENT_IDS.has(talentId);
}

/**
 * Count total Machine Trait levels from all granting talents on a character.
 */
function countMachineTraitLevels(talents: Talent[]): number {
  return talents
    .filter((t) => grantsMachineTrait(t.id))
    .reduce((sum, t) => sum + (t.purchaseCount || 0), 0);
}

export const createTalentsSlice: StateCreator<CharacterStore, [], [], TalentsSlice> = (
  set,
  get
) => ({
  addTalent: (talent: Talent, advancementId?: string, affectedSkillId?: string) => {
    const { character } = get();
    if (!character) return;

    // Normalize the talent to ensure required fields
    const normalizedTalent = ensureTalentFields(talent);

    // Check if this is a "Talented" talent and handle affectedSkillId
    const isTalented = TalentsService.isTalentedTalent(talent.id);

    // For "Talented" talent, try to extract skill ID if not provided
    let skillIdToUse = affectedSkillId;
    if (isTalented && !skillIdToUse) {
      skillIdToUse = TalentsService.extractSkillIdFromTalentedTalent(talent.id);
    }

    // Requirement 4.1, 4.4: Validate "Talented" talent purchases
    if (isTalented) {
      // Requirement 4.4: Require skill selection for "Talented" purchases
      if (!skillIdToUse) {
        console.error('Talented talent requires a target skill selection');
        return; // Prevent purchase without skill selection
      }

      // Requirement 4.1: Prevent duplicate "Talented" for same skill
      const validation = TalentsService.validateTalentedPurchase(character.talents, skillIdToUse);
      if (!validation.valid) {
        console.error(`Invalid Talented purchase: ${validation.error}`);
        return; // Prevent duplicate purchase
      }
    }

    // Check if this talent already exists - match by string ID
    const existingTalentIndex = character.talents.findIndex((t) => t.id === talent.id);

    let updatedCharacter = { ...character };

    if (existingTalentIndex >= 0) {
      // Talent exists - increment purchase count and update history
      const existingTalent = character.talents[existingTalentIndex];
      const currentCount = existingTalent.purchaseCount;
      const purchaseHistory = [...existingTalent.purchaseHistory];

      // Add to purchase history if advancementId provided
      // Requirements 3.2, 3.3: Record advancement purchase with required fields
      if (advancementId) {
        const purchase: TalentPurchase = {
          advancementId,
          purchasedAt: new Date(),
          rank: character.rankTracking?.activeCareerRank ?? 1,
          // For "Talented" talent, include the affected skill ID
          ...(isTalented && skillIdToUse && { affectedSkillId: skillIdToUse })
        };
        purchaseHistory.push(purchase);
      }

      const updatedTalents = [...character.talents];
      updatedTalents[existingTalentIndex] = {
        ...existingTalent,
        purchaseCount: currentCount + 1,
        purchaseHistory,
        // For single-purchase "Talented" talents, store affectedSkillId at talent level
        ...(isTalented &&
          skillIdToUse &&
          purchaseHistory.length === 1 && {
            affectedSkillId: skillIdToUse
          })
      };

      updatedCharacter.talents = updatedTalents;

      // Special handling for Machine Trait granting talents
      if (grantsMachineTrait(talent.id)) {
        updatedCharacter = get().updateMachineTrait(
          updatedCharacter,
          countMachineTraitLevels(updatedTalents)
        );
      }
    } else {
      // Add new talent with initial purchase count
      // Requirements 5.2, 5.4, 5.6: Initialize purchaseCount and purchaseHistory as required fields
      const purchaseHistory: TalentPurchase[] = advancementId
        ? [
            {
              advancementId,
              purchasedAt: new Date(),
              rank: character.rankTracking?.activeCareerRank ?? 1,
              // For "Talented" talent, include the affected skill ID
              ...(isTalented && skillIdToUse && { affectedSkillId: skillIdToUse })
            }
          ]
        : [];

      const newTalent: Talent = {
        ...normalizedTalent,
        purchaseCount: 1, // Always 1 when adding a new talent
        purchaseHistory,
        // For "Talented" talent, store the affected skill ID at talent level
        ...(isTalented && skillIdToUse && { affectedSkillId: skillIdToUse })
      };

      updatedCharacter.talents = [...character.talents, newTalent];

      // Special handling for Machine Trait granting talents
      if (grantsMachineTrait(talent.id)) {
        updatedCharacter = get().updateMachineTrait(
          updatedCharacter,
          countMachineTraitLevels(updatedCharacter.talents)
        );
      }
    }

    // Recompute psychicProfile.psyRating from psy-rating-N talents
    updatedCharacter = recomputePsyRating(updatedCharacter);

    // Auto-save to localStorage
    const finalCharacter = {
      ...updatedCharacter,
      lastModified: new Date()
    };

    try {
      localStorage.setItem('rogue-trader-current-character', JSON.stringify(finalCharacter));
    } catch (error) {
      console.warn('Failed to auto-save character to localStorage:', error);
    }

    set({
      character: finalCharacter,
      hasUnsavedChanges: true
    });
  },

  addTalentPurchase: (
    talentId: string,
    advancementId: string,
    rank: number,
    affectedSkillId?: string
  ) => {
    const { character } = get();
    if (!character) return;

    // Check if this is a "Talented" talent
    const isTalented = TalentsService.isTalentedTalent(talentId);

    // For "Talented" talent, try to extract skill ID if not provided
    let skillIdToUse = affectedSkillId;
    if (isTalented && !skillIdToUse) {
      skillIdToUse = TalentsService.extractSkillIdFromTalentedTalent(talentId);
    }

    // Requirement 4.1, 4.4: Validate "Talented" talent purchases
    if (isTalented) {
      // Requirement 4.4: Require skill selection for "Talented" purchases
      if (!skillIdToUse) {
        console.error('Talented talent requires a target skill selection');
        return; // Prevent purchase without skill selection
      }

      // Requirement 4.1: Prevent duplicate "Talented" for same skill
      const validation = TalentsService.validateTalentedPurchase(character.talents, skillIdToUse);
      if (!validation.valid) {
        console.error(`Invalid Talented purchase: ${validation.error}`);
        return; // Prevent duplicate purchase
      }
    }

    // Use TalentsService to handle the purchase logic
    // This ensures consistent behavior and proper talent creation if needed
    const updatedTalents = TalentsService.addTalentPurchase(
      character.talents,
      talentId,
      advancementId,
      rank,
      skillIdToUse
    );

    // Check if this talent grants Machine Trait levels
    let updatedCharacter: Character = {
      ...character,
      talents: updatedTalents,
      lastModified: new Date()
    };

    if (grantsMachineTrait(talentId)) {
      updatedCharacter = get().updateMachineTrait(
        updatedCharacter,
        countMachineTraitLevels(updatedTalents)
      );
    }

    // Recompute psychicProfile.psyRating from psy-rating-N talents
    updatedCharacter = recomputePsyRating(updatedCharacter);

    // Auto-save to localStorage
    try {
      localStorage.setItem('rogue-trader-current-character', JSON.stringify(updatedCharacter));
    } catch (error) {
      console.warn('Failed to auto-save character to localStorage:', error);
    }

    set({
      character: updatedCharacter,
      hasUnsavedChanges: true
    });
  },

  removeTalentPurchase: (talentId: string, advancementId: string) => {
    const { character } = get();
    if (!character) return;

    // Use TalentsService to handle the removal logic
    const updatedTalents = TalentsService.removeTalentPurchase(
      character.talents,
      talentId,
      advancementId
    );

    // Check if this talent grants Machine Trait levels
    let updatedCharacter: Character = {
      ...character,
      talents: updatedTalents,
      lastModified: new Date()
    };

    if (grantsMachineTrait(talentId)) {
      const totalLevels = countMachineTraitLevels(updatedTalents);
      if (totalLevels > 0) {
        updatedCharacter = get().updateMachineTrait(updatedCharacter, totalLevels);
      } else {
        // Remove Machine trait if no more granting talents remain
        updatedCharacter = {
          ...updatedCharacter,
          traits: updatedCharacter.traits.filter((t) => t.id !== 'machine')
        };
      }
    }

    // Recompute psychicProfile.psyRating from psy-rating-N talents
    updatedCharacter = recomputePsyRating(updatedCharacter);

    // Auto-save to localStorage
    try {
      localStorage.setItem('rogue-trader-current-character', JSON.stringify(updatedCharacter));
    } catch (error) {
      console.warn('Failed to auto-save character to localStorage:', error);
    }

    set({
      character: updatedCharacter,
      hasUnsavedChanges: true
    });
  },

  updateMachineTrait: (character: Character, fleshIsWeakCount: number) => {
    const machineTraitId = 'machine';
    const existingTraitIndex = character.traits.findIndex((t) => t.id === machineTraitId);

    const machineTrait: Trait = {
      id: machineTraitId,
      name: `Machine (${fleshIsWeakCount})`,
      description: `Fashioned from inorganic materials. Does not breathe, immune to vacuum, cold, mind-influencing psychic effects. Armour Points apply to fire damage. Has ${fleshIsWeakCount} Armour Points in all locations.`,
      effect: `${fleshIsWeakCount} AP in all locations. Immune to vacuum, cold, and psychic effects.`,
      rulebook: 'core',
      category: 'cybernetic',
      source: `Flesh is Weak ${fleshIsWeakCount}`,
      mechanics: {
        armourBonus: fleshIsWeakCount,
        immunity: ['vacuum', 'cold', 'mind-influencing-psychic']
      },
      parameters: { rating: fleshIsWeakCount }
    };

    if (existingTraitIndex >= 0) {
      // Update existing trait
      const updatedTraits = [...character.traits];
      updatedTraits[existingTraitIndex] = machineTrait;
      return { ...character, traits: updatedTraits };
    } else {
      // Add new trait
      return { ...character, traits: [...character.traits, machineTrait] };
    }
  },

  removeTalent: (talentId: string) => {
    const { character } = get();
    if (!character) return;

    // Check if this talent grants Machine Trait levels
    const removedTalent = character.talents.find((t) => t.id === talentId);
    let updatedCharacter: Character = {
      ...character,
      talents: character.talents.filter((talent) => talent.id !== talentId),
      lastModified: new Date()
    };

    // If removing a Machine Trait granting talent, recalculate
    if (removedTalent && grantsMachineTrait(removedTalent.id)) {
      const totalLevels = countMachineTraitLevels(updatedCharacter.talents);
      if (totalLevels > 0) {
        updatedCharacter = get().updateMachineTrait(updatedCharacter, totalLevels);
      } else {
        updatedCharacter = {
          ...updatedCharacter,
          traits: updatedCharacter.traits.filter((t) => t.id !== 'machine')
        };
      }
    }

    // Recompute psychicProfile.psyRating from psy-rating-N talents
    updatedCharacter = recomputePsyRating(updatedCharacter);

    // Auto-save to localStorage
    try {
      localStorage.setItem('rogue-trader-current-character', JSON.stringify(updatedCharacter));
    } catch (error) {
      console.warn('Failed to auto-save character to localStorage:', error);
    }

    set({
      character: updatedCharacter,
      hasUnsavedChanges: true
    });
  },

  getTalentById: (talentId: string) => {
    const { character } = get();
    if (!character) return undefined;

    return character.talents.find((talent) => talent.id === talentId);
  },

  hasTalentPurchase: (talentId: string, advancementId: string) => {
    const { character } = get();
    if (!character) return false;

    const talent = character.talents.find((t) => t.id === talentId);
    if (!talent) return false;

    return TalentsService.hasAdvancementPurchase(talent, advancementId);
  },

  skillHasTalentedBonus: (skillId: string) => {
    const { character } = get();
    if (!character) return false;

    return TalentsService.skillHasTalentedBonus(character.talents, skillId);
  },

  getSkillsWithTalentedBonus: () => {
    const { character } = get();
    if (!character) return [];

    return TalentsService.getSkillsWithTalentedBonus(character.talents);
  },

  addTrait: (trait: Trait) => {
    const { character } = get();
    if (!character) return;

    const updatedCharacter = {
      ...character,
      traits: [...character.traits, trait],
      lastModified: new Date()
    };

    // Auto-save to localStorage
    try {
      localStorage.setItem('rogue-trader-current-character', JSON.stringify(updatedCharacter));
    } catch (error) {
      console.warn('Failed to auto-save character to localStorage:', error);
    }

    set({
      character: updatedCharacter,
      hasUnsavedChanges: true
    });
  },

  removeTrait: (traitId: string) => {
    const { character } = get();
    if (!character) return;

    const updatedCharacter = {
      ...character,
      traits: character.traits.filter((trait) => trait.id !== traitId),
      lastModified: new Date()
    };

    // Auto-save to localStorage
    try {
      localStorage.setItem('rogue-trader-current-character', JSON.stringify(updatedCharacter));
    } catch (error) {
      console.warn('Failed to auto-save character to localStorage:', error);
    }

    set({
      character: updatedCharacter,
      hasUnsavedChanges: true
    });
  }
});
